import { useCallback, useEffect, useState } from "react";
import { Button } from "reactstrap";

import useFetch from "Hooks/useFetch";
import useUserContext from "Hooks/useUserContext";
import useAlertContext from "Hooks/useAlertContext";
import { HideOrShowPassword } from "Pages/Setup/Configuration/Components/HideOrShowPassword/HideOrShowPassword";
import styles from "Pages/Setup/Connect/Onboarding.module.css";

export type ActionType = "new" | "edit" | "delete" | "";

/** A config row as returned by GET /api/configuration/{company_id}. */
export interface IConfig {
    keyName: string;
    keyValue: string;
    is_obscured?: boolean;
}

/** Declarative description of one editable config field. `suffix` is the part
 * of the key after the `{company}.` prefix (matched case-insensitively). */
export interface FieldDef {
    suffix: string;
    label: string;
    help?: string;
    secret?: boolean;
    required?: boolean;
    type?: "text" | "boolean";
}

/**
 * HideOrShowPassword is itself a hook-using component, so it must not be called
 * as a bare function from a conditional branch (that lands its hooks in the
 * caller and the count shifts as data loads). Wrapping it in a real component
 * isolates its hooks, so we can mount/unmount it freely.
 */
function ObscuredValue({ value, row }: { value: string; row: number }) {
    return <>{HideOrShowPassword(value, row)}</>;
}

/**
 * Reads/writes the logged-in company's config straight from the DB
 * (`/api/configuration/{company}`) — NOT from GetTests — so values appear
 * regardless of whether a connection test is registered. Owns the EditModal
 * state; render a single <EditModal {...modalProps}/> per page.
 */
export function useCompanyConfig() {
    const [configs, setConfigs] = useState<IConfig[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(undefined);
    const [activeModal, setActiveModal] = useState<ActionType>("");
    const [prefillKey, setPrefillKey] = useState<string>("");

    const [userContext] = useUserContext();
    const { get, post } = useFetch();
    const { newAlert } = useAlertContext();
    const companyId = userContext.companyId;

    /** Reload config rows from the server so OAuth status can update live. */
    const refreshConfigs = useCallback(
        async (showErrorAlert = true): Promise<boolean> => {
            if (!companyId) return false;
            const response = await get(`/api/configuration/${companyId}`);
            if (!response.ok) {
                if (showErrorAlert) {
                    newAlert({ message: "Error getting configuration" });
                }
                setLoaded(true);
                return false;
            }
            const data = await response.json();
            setConfigs(Array.isArray(data) ? data : []);
            setLoaded(true);
            return true;
        },
        [companyId, get, newAlert]
    );

    useEffect(() => {
        let ignore = false;
        (async function loadInitialConfigs() {
            if (!companyId) return;
            const ok = await refreshConfigs(true);
            if (ignore && ok) return;
        })();
        return () => {
            ignore = true;
        };
    }, [companyId, refreshConfigs]);

    const onSetActiveModal = useCallback((m: ActionType) => setActiveModal(m), []);
    const onSetSelectedRow = useCallback((r: any) => setSelectedRow(r), []);

    /** Reflect a saved create/edit back into local state without a refetch. */
    const updateConfigs = useCallback(
        (data: { keyName: string; keyValue: string }) => {
            setConfigs((prev) => {
                const exists = prev.some(
                    (c) =>
                        c.keyName?.toLowerCase() === data.keyName?.toLowerCase()
                );
                return exists
                    ? prev.map((c) =>
                          c.keyName?.toLowerCase() ===
                          data.keyName?.toLowerCase()
                              ? { ...c, ...data }
                              : c
                      )
                    : [...prev, data];
            });
        },
        []
    );

    const findConfig = useCallback(
        (suffix: string): IConfig | undefined =>
            configs.find((c) =>
                c.keyName?.toLowerCase().endsWith(suffix.toLowerCase())
            ),
        [configs]
    );

    /** Upsert one config value without opening the modal (used by checkboxes). */
    const saveField = useCallback(
        async (suffix: string, value: string): Promise<boolean> => {
            if (!companyId) return false;

            const existing = configs.find((c) =>
                c.keyName?.toLowerCase().endsWith(suffix.toLowerCase())
            );
            const mode: "new" | "edit" = existing ? "edit" : "new";
            const payload = {
                keyName: existing?.keyName ?? `${companyId}.${suffix}`,
                keyValue: value,
            };

            const response = await post(
                `/api/configuration/${mode}/${companyId}`,
                payload
            );
            let data: any = null;
            try {
                data = await response.json();
            } catch {
                data = null;
            }

            if (!response.ok) {
                newAlert({
                    isOpen: true,
                    message: "Error saving configuration",
                    color: "danger",
                });
                return false;
            }

            updateConfigs(data?.keyName ? data : payload);
            return true;
        },
        [companyId, configs, post, updateConfigs, newAlert]
    );

    /**
     * Open EditModal for a field. For an existing key → "edit"; for a missing
     * key → "new" with the full company-prefixed key pre-filled (and locked),
     * so the user only supplies the value.
     */
    const editField = useCallback(
        (suffix: string, secret: boolean, index: number) => {
            const config = configs.find((c) =>
                c.keyName?.toLowerCase().endsWith(suffix.toLowerCase())
            );
            if (!config) {
                setPrefillKey(`${companyId}.${suffix}`);
                setSelectedRow(undefined);
                setActiveModal("new");
                return;
            }
            setPrefillKey("");
            setSelectedRow({
                index,
                values: { keyName: config.keyName, keyValue: config.keyValue },
                original: {
                    keyName: config.keyName,
                    keyValue: config.keyValue,
                    is_obscured: secret,
                },
            });
            setActiveModal("edit");
        },
        [configs, companyId]
    );

    const modalProps = {
        activeModal,
        updateConfigs,
        onSetActiveModal,
        selectedRow,
        onSetSelectedRow,
        prefillKey,
    };

    return {
        configs,
        loaded,
        companyId,
        findConfig,
        editField,
        saveField,
        refreshConfigs,
        modalProps,
    };
}

/** One editable config field — label + raw key + value + Set/Edit + flag. */
export function ConfigField({
    field,
    config,
    index,
    onEdit,
}: {
    field: FieldDef;
    config: IConfig | undefined;
    index: number;
    onEdit: (suffix: string, secret: boolean, index: number) => void;
}) {
    const isSet = !!config?.keyValue;
    const required = !!field.required;
    const showMissing = !isSet && required;
    return (
        <div className={styles.field}>
            <div className={styles.fieldHead}>
                <label className={styles.label}>
                    {field.label}
                    {required && <span className={styles.req}>*</span>}
                </label>
                <code className={styles.key}>{field.suffix}</code>
            </div>
            {field.help && <p className={styles.help}>{field.help}</p>}
            <div
                className={`${styles.valueRow} ${
                    showMissing ? styles.valueRowMissing : ""
                }`}
            >
                <span className={styles.value}>
                    {isSet ? (
                        field.secret ? (
                            <ObscuredValue value={config!.keyValue} row={index} />
                        ) : (
                            config!.keyValue
                        )
                    ) : (
                        <span className={styles.valueEmpty}>Not set</span>
                    )}
                </span>
                <Button
                    size="sm"
                    color={isSet ? "secondary" : "primary"}
                    outline={isSet}
                    onClick={() => onEdit(field.suffix, !!field.secret, index)}
                >
                    {isSet ? "Edit" : "Set value"}
                </Button>
            </div>
            <span
                className={`${styles.flag} ${
                    isSet
                        ? styles.flagSet
                        : required
                        ? styles.flagMissing
                        : styles.flagOptional
                }`}
            >
                {isSet
                    ? "✓ Set"
                    : required
                    ? "⚠ Needs a value"
                    : "Optional"}
            </span>
        </div>
    );
}
